import React, { useMemo, useState } from "react";
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    Link as MuiLink,
    Paper,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
    Collapse,
} from "@mui/material";
import {
    Add as AddIcon,
    AutoAwesome as AutoAwesomeIcon,
    CheckCircleOutline as CheckCircleOutlineIcon,
    DeleteOutline as DeleteOutlineIcon,
    EditOutlined as EditOutlinedIcon,
    ErrorOutline as ErrorOutlineIcon,
    ScheduleOutlined as ScheduleOutlinedIcon,
    ExpandLess as ExpandLessIcon,
    ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import { FormattedMessage, useIntl } from "react-intl";
import { useSnackbar } from "notistack";
import insightsApi from "../../api/insightsApi";
import { roleRefusalMessage } from "../../utils/projectRoles";
import ActivityForm from "./ActivityForm";
import SuggestionVerdict from "./SuggestionVerdict";
import { asSegments, byRank, placementOf } from "./segments";
import {
    ACTIVITY_STATUSES,
    OVERDUE,
    STATUS_BADGE,
    asDashboard,
    byPlannedDate,
    donePercent,
    formatDate,
    groupActivities,
    isOverdue,
    isSuggested,
    normalizeStatus,
    statusLabel,
    tally,
} from "./activityConstants";

// Status is a fixed scale, not a series palette, so each tile carries an ICON as
// well as its colour and its label — the colour is never the only channel.
const TILES = [
    { key: "done", Icon: CheckCircleOutlineIcon },
    { key: "coming", Icon: ScheduleOutlinedIcon },
    { key: "outstanding", Icon: ErrorOutlineIcon },
];

// One shared empty set, so the "no exceptions on this tab" case is a stable
// reference rather than a new Set on every render.
const EMPTY_KEYS = new Set();

const ALL_STATUSES = "all";
const BY_IMPACT = "impact";
const BY_SEGMENT = "segment";

// Both segmented controls, one shape. A pill group says "pick a mode" where a
// tab strip says "go somewhere", and this screen already sits under two real tab
// strips — the page's and the workspace's.
const segmentedSx = {
    backgroundColor: "rgba(0,0,0,0.04)",
    borderRadius: 999,
    p: 0.5,
    "& .MuiToggleButtonGroup-grouped": {
        border: 0,
        borderRadius: "999px !important",
        textTransform: "none",
        fontWeight: 600,
        px: 1.75,
        py: 0.5,
        color: "text.secondary",
        "&.Mui-selected": {
            backgroundColor: "#fff",
            color: "#00438E",
            boxShadow: "0 1px 3px rgba(0,0,0,0.16)",
            "&:hover": { backgroundColor: "#fff" },
        },
    },
};

// One headline number. A stat tile, not a one-bar chart: three counts and a
// total are a handful of numbers, and the number IS the chart.
function StatTile({ statusKey, Icon, value }) {
    const intl = useIntl();
    const meta = STATUS_BADGE[statusKey];
    return (
        <Paper
            variant="outlined"
            sx={{ px: 2, py: 1.5, borderRadius: 2, minWidth: 118, flexGrow: 1, flexBasis: 0 }}
        >
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
                <Icon sx={{ fontSize: "1rem", color: meta.color }} />
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    {statusLabel(intl, statusKey)}
                </Typography>
            </Stack>
            {/* Proportional figures deliberately: tabular-nums would make a
                two-digit value look loose at this size. */}
            <Typography sx={{ fontSize: "1.75rem", fontWeight: 600, lineHeight: 1.1, color: meta.color }}>
                {value}
            </Typography>
        </Paper>
    );
}

// done / total as a meter: a single ratio against a limit, so a bar with a track
// rather than a two-slice pie. The unfilled track is a lighter step of the fill's
// own ramp, so the state reads across the whole bar and not just the fill.
function ProgressMeter({ done, total, width }) {
    const percent = donePercent({ done, total });
    const complete = total > 0 && done === total;
    const fill = complete ? STATUS_BADGE.done.color : "#00438E";
    return (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: width || 120 }}>
            <Box
                sx={{
                    position: "relative",
                    flexGrow: 1,
                    minWidth: 48,
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: complete ? "rgba(76,175,80,0.22)" : "rgba(0,67,142,0.14)",
                    overflow: "hidden",
                }}
            >
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        width: `${percent}%`,
                        borderRadius: 3,
                        backgroundColor: fill,
                    }}
                />
            </Box>
            <Typography
                variant="caption"
                sx={{ color: "text.secondary", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}
            >
                {done}/{total}
            </Typography>
        </Stack>
    );
}

// The status chip IS the quick status change: the thing you read to learn where
// an activity has got to is the thing you click to move it. One click advances to
// the next state, which is the move nine times in ten; the panel's menu is there
// for the tenth.
// Without `onChange` (a project viewer) it is just the badge.
function StatusControl({ value, busy, onChange }) {
    const intl = useIntl();
    const key = normalizeStatus(value);
    const meta = STATUS_BADGE[key];
    if (!onChange) {
        return (
            <Chip
                size="small"
                label={statusLabel(intl, key)}
                sx={{ backgroundColor: meta.bg, color: meta.color, fontWeight: 600, flexShrink: 0 }}
            />
        );
    }
    const next = ACTIVITY_STATUSES[(ACTIVITY_STATUSES.indexOf(key) + 1) % ACTIVITY_STATUSES.length];
    return (
        <Chip
            size="small"
            label={statusLabel(intl, key)}
            disabled={busy}
            onClick={() => onChange(next)}
            title={intl.formatMessage(
                { id: "activity.advance_status", defaultMessage: "Change status to {status}" },
                { status: statusLabel(intl, next) }
            )}
            sx={{
                backgroundColor: meta.bg,
                color: meta.color,
                fontWeight: 600,
                flexShrink: 0,
                "&:hover": { backgroundColor: meta.bg, filter: "brightness(0.95)" },
            }}
        />
    );
}

// One activity, wherever it is grouped. Deliberately identical across the two
// groupings: only the section headers change, so a row means the same thing
// whichever fold you are looking at.
// `onAddForImpact` is only passed in the folds where the section is NOT an
// impact — grouped by segment, where the impact is a caption on the
// row and the row is therefore the only per-impact anchor there is.
function ActivityRow({
    activity,
    showImpact,
    busy,
    onStatus,
    onAccept,
    onReject,
    onOpen,
    onDelete,
    onAddForImpact,
    // A project viewer: the row's facts, none of its controls.
    readOnly = false,
}) {
    const intl = useIntl();
    const overdue = isOverdue(activity);
    const suggested = isSuggested(activity);
    const planned = formatDate(intl, activity.planned_date);

    return (
        <Paper
            variant="outlined"
            sx={{
                px: 1.5,
                py: 1,
                borderRadius: 2,
                ...(suggested
                    ? { borderColor: "rgba(0,67,142,0.35)", backgroundColor: "rgba(0,67,142,0.03)" }
                    : {}),
            }}
        >
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Box sx={{ flexGrow: 1, minWidth: 200 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {activity.title}
                        </Typography>
                        {suggested && (
                            <Chip
                                size="small"
                                icon={<AutoAwesomeIcon />}
                                label={intl.formatMessage({
                                    id: "activity.suggested",
                                    defaultMessage: "Suggested",
                                })}
                                sx={{
                                    backgroundColor: "rgba(0,67,142,0.10)",
                                    color: "#00438E",
                                    fontWeight: 600,
                                    "& .MuiChip-icon": { color: "#00438E", fontSize: "0.85rem" },
                                }}
                            />
                        )}
                    </Stack>
                    <Stack direction="row" spacing={1} sx={{ mt: 0.25 }} flexWrap="wrap" useFlexGap>
                        {/* Which impact this is against — the one piece of context
                            a row loses when it is grouped by segment. */}
                        {showImpact && (
                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                {activity.impact_name}
                            </Typography>
                        )}
                    </Stack>
                </Box>

                {/* The date, and the one red on this screen. Overdue is a different
                    question from status — an activity can be outstanding and
                    perfectly on time — so it gets its own marker rather than
                    recolouring the status chip. */}
                {planned ? (
                    <Chip
                        size="small"
                        variant={overdue ? "filled" : "outlined"}
                        icon={overdue ? <ErrorOutlineIcon /> : undefined}
                        label={
                            overdue
                                ? intl.formatMessage(
                                      { id: "activity.overdue_on", defaultMessage: "Overdue · {date}" },
                                      { date: planned }
                                  )
                                : planned
                        }
                        sx={{
                            flexShrink: 0,
                            fontVariantNumeric: "tabular-nums",
                            ...(overdue
                                ? {
                                      backgroundColor: OVERDUE.bg,
                                      color: OVERDUE.color,
                                      fontWeight: 700,
                                      "& .MuiChip-icon": { color: OVERDUE.color, fontSize: "0.85rem" },
                                  }
                                : { color: "text.secondary" }),
                        }}
                    />
                ) : (
                    <Typography
                        variant="caption"
                        sx={{ color: "text.disabled", fontStyle: "italic", flexShrink: 0 }}
                    >
                        <FormattedMessage id="activity.no_date" defaultMessage="No date" />
                    </Typography>
                )}

                <StatusControl
                    value={activity.status}
                    busy={busy}
                    onChange={readOnly ? undefined : (next) => onStatus(activity, next)}
                />

                {/* A proposal is answered before it is managed: the verdict
                    pair sits ahead of the row's own controls, and stands in for
                    the delete icon below — rejecting IS the delete. */}
                {suggested && !readOnly && (
                    <SuggestionVerdict
                        title={activity.title}
                        busy={busy}
                        onAccept={() => onAccept(activity)}
                        onReject={() => onReject(activity)}
                    />
                )}

                {!readOnly && (
                <Stack direction="row" sx={{ flexShrink: 0 }}>
                    {/* Editing opens the impact's Activities panel — the one place
                        the form lives, so a row edited from here behaves exactly
                        like one edited from the register. */}
                    <IconButton
                        size="small"
                        disabled={busy}
                        onClick={() => onOpen(activity.impact_id)}
                        aria-label={intl.formatMessage(
                            { id: "activity.edit_named", defaultMessage: "Edit {title}" },
                            { title: activity.title }
                        )}
                    >
                        <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                    {onAddForImpact && (
                        <AddActivityButton
                            icon
                            section={{
                                impactId: activity.impact_id,
                                impactLabel: activity.impact_name,
                            }}
                            onAdd={() => onAddForImpact(activity)}
                        />
                    )}
                    {!suggested && (
                        <IconButton
                            size="small"
                            disabled={busy}
                            onClick={() => onDelete(activity)}
                            aria-label={intl.formatMessage(
                                { id: "activity.delete_named", defaultMessage: "Delete {title}" },
                                { title: activity.title }
                            )}
                            sx={{ color: "text.disabled", "&:hover": { color: "error.main" } }}
                        >
                            <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                    )}
                </Stack>
                )}
            </Stack>
        </Paper>
    );
}

// "Add activity" for ONE impact, wherever that impact is on screen.
//
// It reads "Add activity" on screen because the thing it sits on already says
// which impact; out of that context — in a screen reader's list of buttons —
// every one of them would be the same name, so the accessible name carries the
// impact. Same reasoning the zero-activity buttons already used; this is that
// pattern, pulled out so the section header and the activity row cannot drift.
function AddActivityButton({ section, onAdd, icon = false }) {
    const intl = useIntl();
    // A segment section has no single impact: its button is named by the
    // segments, and the form asks which impact (see openAddFor).
    const label = intl.formatMessage(
        { id: "activity.add_to_impact", defaultMessage: "Add activity to {impact}" },
        { impact: section.impactLabel ?? section.label }
    );
    if (icon) {
        // The row variant. A row already carries four controls, so this is a
        // glyph rather than a fifth labelled button — and it needs the tooltip
        // to say WHICH impact, because a row grouped by area is the only place
        // the impact is not the heading above it.
        return (
            <Tooltip title={label} describeChild>
                <IconButton
                    size="small"
                    onClick={() => onAdd(section)}
                    aria-label={label}
                    sx={{ color: "#00438E" }}
                >
                    <AddIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        );
    }
    return (
        <Button
            size="small"
            startIcon={<AddIcon fontSize="small" />}
            onClick={() => onAdd(section)}
            aria-label={label}
            sx={{ textTransform: "none", color: "#00438E", flexShrink: 0 }}
        >
            <FormattedMessage id="activity.add" defaultMessage="Add activity" />
        </Button>
    );
}

// "What's done, what's missing?" for the whole project — and since the same
// question gets asked of a department, of a discipline and of a single impact,
// the same feed folded three ways.
//
// Every view is a fold over `dashboard.activities`, the BE's flat list. One
// payload, several views: an endpoint per view would drift out of agreement about
// which activities exist the moment one was cached.
export default function ActivityDashboard({
    projectId,
    dashboard,
    // No `registerImpacts`. It existed to populate the global Add form's impact
    // picker; the per-impact forms know their impact from the row that opened
    // them, so the whole register no longer has to be handed down.
    loading,
    error,
    onRefresh,
    onOpenImpact,
    // False for a project viewer: no Add, no row controls.
    canEdit = true,
    // The project's segments and the register's impacts (which carry
    // `segment_values`): the dashboard payload has neither, and they are what
    // the Segment fold and each impact's caption are built from.
    segments: segmentsProp = [],
    registerImpacts = [],
}) {
    const intl = useIntl();
    const { enqueueSnackbar } = useSnackbar();
    const { totals, impacts, activities } = asDashboard(dashboard);

    // Which impact an inline "New activity" form is open for, and the section it
    // was opened from: { impactId, impactLabel, sectionKey }.
    //
    // A boolean before, because there was one form and it asked you to pick an
    // impact. Scoping it to an impact is what lets the form drop that question —
    // and keying it to a section too is what puts the form where you clicked,
    // instead of one shared form at the top of the page for a row halfway down.
    const [addFor, setAddFor] = useState(null);
    const [saving, setSaving] = useState(false);
    const [busyId, setBusyId] = useState(null);
    const [status, setStatus] = useState(ALL_STATUSES);
    const [groupBy, setGroupBy] = useState(BY_IMPACT);
    // Which sections the user has clicked AGAINST the current default, and the
    // tab those clicks were made on. Storing the exceptions rather than the open
    // set means a section that appears later (a new impact, a re-group) still
    // obeys the tab's default instead of arriving in whatever state an
    // initialiser happened to capture.
    //
    // Each tab starts from its own default, so switching clears them — but in the
    // SAME update as the tab change, not in an effect afterwards. An effect runs
    // after the render that changed the tab, so for one paint a section the user
    // had opened under "All" came up collapsed in a status tab: the opposite of
    // what the tab promises. The stored tab is the belt to that braces — a stale
    // exception set can never apply to the tab now showing.
    const [toggled, setToggled] = useState({ status: ALL_STATUSES, keys: EMPTY_KEYS });

    // "All" opens COLLAPSED: it is the overview, and every section expanded is a
    // wall of rows. A status tab opens EXPANDED — you filtered precisely so you
    // could see the matching activities without another click, and that holds
    // however few match: one activity is still the activity you filtered for.
    const sectionsOpenByDefault = status !== ALL_STATUSES;
    const exceptions = toggled.status === status ? toggled.keys : EMPTY_KEYS;
    const isSectionOpen = (key) =>
        exceptions.has(key) ? !sectionsOpenByDefault : sectionsOpenByDefault;
    const toggleSection = (key) =>
        setToggled((prev) => {
            const keys = new Set(prev.status === status ? prev.keys : EMPTY_KEYS);
            if (keys.has(key)) keys.delete(key);
            else keys.add(key);
            return { status, keys };
        });

    const segments = useMemo(() => asSegments(segmentsProp), [segmentsProp]);
    // impact id -> { key, label, rank } — where it sits across every segment.
    const placementById = useMemo(
        () => new Map(registerImpacts.map((impact) => [String(impact.id), placementOf(impact, segments)])),
        [registerImpacts, segments]
    );
    const anySegment = intl.formatMessage({
        id: "activity.segment_any",
        defaultMessage: "Any segment",
    });

    // The status filter scopes every grouped view. Applied before the fold, so a
    // section's own counts describe what you are actually looking at.
    const filtered = useMemo(
        () =>
            status === ALL_STATUSES
                ? activities
                : activities.filter((a) => normalizeStatus(a.status) === status),
        [activities, status]
    );

    // The two folds. By impact is seeded from the rollup so an impact with
    // NOTHING planned keeps its section — that empty section is the whole reason
    // this screen exists, and dropping it because the feed has no row for it
    // would answer the opposite question.
    //
    // That seeding is right on "All" and wrong on a STATUS tab. Under Outstanding
    // the question is "what is outstanding", so an impact with nothing
    // outstanding is not an answer to it — it is noise, and there is one section
    // of it per impact in the project. Those tabs therefore drop the seeded
    // sections that nothing survived into, rather than rendering a header over a
    // "no activities match this filter" note. "All" keeps every impact, empty
    // ones included, because there the empty section IS the finding.
    const sections = useMemo(() => {
        if (groupBy === BY_SEGMENT) {
            // One section per COMBINATION of choices an impact sits in — Role and
            // Tenure together, or Role alone when it is Any in Tenure. An impact
            // placed nowhere lands in "Any segment", last.
            const keyOf = (activity) => placementById.get(String(activity.impact_id))?.key ?? "";
            const byKey = new Map();
            for (const placement of placementById.values()) byKey.set(placement.key, placement);
            // The impacts in each combination: what the section's Add offers.
            const impactsByKey = new Map();
            for (const impact of registerImpacts) {
                const key = placementById.get(String(impact.id))?.key ?? "";
                if (!impactsByKey.has(key)) impactsByKey.set(key, []);
                impactsByKey.get(key).push({ id: impact.id, what_is_changing: impact.what_is_changing });
            }
            // On "All" a combination with impacts but nothing planned still gets
            // its section, as an impact does in the Impact fold — that is where
            // its first activity is added from.
            const keys = new Set(filtered.map(keyOf));
            if (status === ALL_STATUSES) for (const key of impactsByKey.keys()) keys.add(key);
            const order = [...keys].sort((a, b) =>
                byRank(byKey.get(a)?.rank || [], byKey.get(b)?.rank || []) || (a === "" ? 1 : b === "" ? -1 : 0)
            );
            const folded = new Map(
                groupActivities(filtered, keyOf, (key) => byKey.get(key)?.label || anySegment, order).map(
                    (section) => [section.key, section]
                )
            );
            return order.map((key) => ({
                ...(folded.get(key) || {
                    key,
                    label: byKey.get(key)?.label || anySegment,
                    activities: [],
                    counts: tally([]),
                }),
                impacts: impactsByKey.get(key) || [],
            }));
        }
        const byImpact = new Map();
        for (const row of impacts) byImpact.set(String(row.impact_id), []);
        for (const activity of filtered) {
            const key = String(activity.impact_id);
            if (!byImpact.has(key)) byImpact.set(key, []);
            byImpact.get(key).push(activity);
        }
        const rowOf = new Map(impacts.map((row) => [String(row.impact_id), row]));
        return [...byImpact.entries()]
            .filter(([, rows]) => status === ALL_STATUSES || rows.length > 0)
            .map(([key, rows]) => {
                const row = rowOf.get(key);
                const impactLabel = row?.name || rows[0]?.impact_name || key;
                return {
                    key,
                    impactId: Number(key),
                    label: impactLabel,
                    // The same string as `label` here, kept as its own field
                    // because AddActivityButton is also handed synthetic sections
                    // built from a single ROW (grouped by segment, where
                    // `label` is the SEGMENTS and the impact is on the row).
                    impactLabel,
                    // Where the impact sits — the same words the register's
                    // Segments column uses.
                    sublabel: placementById.get(key)?.label || "",
                    activities: [...rows].sort(byPlannedDate),
                    counts: tally(rows),
                    // The rollup's total, BEFORE the status filter. `counts` is what
                    // survived the filter, so it cannot tell "this impact has nothing
                    // planned" from "nothing of this impact matches the tab" — and
                    // calling the second one a gap would be a lie.
                    totalAll: row?.total ?? rows.length,
                };
            });
    }, [groupBy, filtered, status, impacts, registerImpacts, placementById, anySegment]);

    const overdueCount = useMemo(() => activities.filter((a) => isOverdue(a)).length, [activities]);

    const detailFrom = (err) => {
        // A role refusal's body is a code, not a sentence.
        const refused = roleRefusalMessage(err, intl);
        if (refused) return refused;
        const data = err?.response?.data;
        if (!data || typeof data !== "object" || Array.isArray(data)) return null;
        return Object.values(data).flat().find((m) => typeof m === "string") || null;
    };

    const shout = (err, id, fallback) =>
        enqueueSnackbar(detailFrom(err) || intl.formatMessage({ id, defaultMessage: fallback }), {
            variant: "error",
        });

    // ── Row actions. Every one is a call plus a refetch: the feed is the BE's
    // fold, and patching a row in here is how the counts start disagreeing with
    // the list beneath them. ──
    const runOnActivity = async (activity, work, failId, failMsg) => {
        try {
            setBusyId(activity.id);
            await work();
            await onRefresh?.();
        } catch (err) {
            console.error(failMsg, err);
            shout(err, failId, failMsg);
        } finally {
            setBusyId(null);
        }
    };

    const handleStatus = (activity, next) =>
        runOnActivity(
            activity,
            () =>
                insightsApi.updateActivity(projectId, activity.impact_id, activity.id, {
                    status: next,
                }),
            "activity.status_change_failed",
            "Failed to change the status"
        );

    // Accepting a proposal. `approved` alone, like the status change: the row is
    // being taken as written, not rewritten.
    const handleAccept = (activity) =>
        runOnActivity(
            activity,
            () =>
                insightsApi.updateActivity(projectId, activity.impact_id, activity.id, {
                    approved: true,
                }),
            "activity.accept_failed",
            "Couldn't accept the suggestion"
        );

    // Rejecting one. The same DELETE as the bin on a normal row, and no
    // confirmation: this is the review step itself, the row was never anyone's
    // plan, and the AI will propose again.
    const handleReject = (activity) =>
        runOnActivity(
            activity,
            () => insightsApi.deleteActivity(projectId, activity.impact_id, activity.id),
            "activity.reject_failed",
            "Couldn't reject the suggestion"
        );

    const handleDelete = (activity) =>
        runOnActivity(
            activity,
            () => insightsApi.deleteActivity(projectId, activity.impact_id, activity.id),
            "activity.delete_failed",
            "Failed to delete the activity"
        );

    // Open the inline form for one impact. Takes the SECTION rather than an id so
    // the form can be rendered where it was opened from and can name the impact
    // it is adding to — which is the only way the user can tell, now that the
    // form has no impact field.
    // A segment section holds several impacts, so its Add asks which one —
    // unless the combination holds exactly one, which answers the question.
    const openAddFor = (section) => {
        const choices = section.impactId == null ? section.impacts || [] : [];
        setAddFor({
            impactId: choices.length === 1 ? choices[0].id : section.impactId,
            impactLabel: choices.length === 1 ? choices[0].what_is_changing : section.impactLabel,
            segmentLabel: choices.length > 1 ? section.label : null,
            impacts: choices.length > 1 ? choices : null,
            sectionKey: section.key,
        });
    };

    // The impact comes from `addFor`, or — when the section offered a choice —
    // from the form's own picker, handed back as `onSave`'s second argument.
    const handleAdd = async (payload, pickedImpactId) => {
        const impactId = pickedImpactId ?? addFor?.impactId;
        if (!impactId) return;
        try {
            setSaving(true);
            await insightsApi.createActivity(projectId, impactId, payload);
            setAddFor(null);
            await onRefresh?.();
            enqueueSnackbar(
                intl.formatMessage({ id: "activity.saved", defaultMessage: "Activity saved" }),
                { variant: "success" }
            );
        } catch (err) {
            console.error("Failed to create activity:", err);
            shout(err, "activity.save_failed", "Failed to save the activity");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress sx={{ color: "#00438E" }} />
            </Box>
        );
    }

    if (error) {
        return (
            <Paper variant="outlined" sx={{ p: 6, borderRadius: 2, textAlign: "center" }}>
                <Typography sx={{ color: "text.secondary", mb: 2 }}>
                    <FormattedMessage
                        id="activity.dashboard_failed"
                        defaultMessage="Couldn't load the activity dashboard."
                    />
                </Typography>
                <Button
                    variant="outlined"
                    onClick={onRefresh}
                    sx={{ color: "#00438E", borderColor: "#00438E" }}
                >
                    <FormattedMessage id="impact.ai_retry" defaultMessage="Retry" />
                </Button>
            </Paper>
        );
    }

    return (
        <Box>
            {/* ── The headline ── */}
            <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={2}
                alignItems={{ xs: "stretch", md: "center" }}
                sx={{ mb: 3 }}
            >
                {/* The one figure the screen leads with. Exactly one per view. */}
                <Box sx={{ minWidth: 168 }}>
                    <Typography sx={{ fontSize: "3rem", fontWeight: 600, lineHeight: 1, color: "#00438E" }}>
                        {donePercent(totals)}%
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                        <FormattedMessage
                            id="activity.percent_done"
                            defaultMessage="of {total, number} {total, plural, one {activity} other {activities}} done"
                            values={{ total: totals.total }}
                        />
                    </Typography>
                </Box>

                <Stack direction="row" spacing={1.5} sx={{ flexGrow: 1 }} flexWrap="wrap" useFlexGap>
                    {TILES.map(({ key, Icon }) => (
                        <StatTile key={key} statusKey={key} Icon={Icon} value={totals[key]} />
                    ))}
                </Stack>
            </Stack>

            {/* ── Filters, in one row above everything they scope ── */}
            <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
                useFlexGap
                sx={{ mb: 2 }}
            >
                <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={status}
                    // `null` when the pressed button is clicked again — a segmented
                    // control has no empty state, so that is a no-op.
                    onChange={(_e, next) => {
                        if (!next) return;
                        setStatus(next);
                        // Cleared here rather than in an effect: see `toggled`.
                        setToggled({ status: next, keys: EMPTY_KEYS });
                    }}
                    aria-label={intl.formatMessage({
                        id: "activity.filter_status",
                        defaultMessage: "Status",
                    })}
                    sx={segmentedSx}
                >
                    <ToggleButton value={ALL_STATUSES}>
                        <FormattedMessage id="activity.filter_all" defaultMessage="All" />
                    </ToggleButton>
                    {ACTIVITY_STATUSES.map((key) => (
                        <ToggleButton key={key} value={key}>
                            {statusLabel(intl, key)}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>

                <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                    {overdueCount > 0 && (
                        <Chip
                            size="small"
                            icon={<ErrorOutlineIcon />}
                            label={intl.formatMessage(
                                { id: "activity.overdue_count", defaultMessage: "{count, number} overdue" },
                                { count: overdueCount }
                            )}
                            sx={{
                                backgroundColor: OVERDUE.bg,
                                color: OVERDUE.color,
                                fontWeight: 700,
                                "& .MuiChip-icon": { color: OVERDUE.color, fontSize: "0.85rem" },
                            }}
                        />
                    )}
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        <FormattedMessage id="activity.group_by" defaultMessage="Group by" />
                    </Typography>
                    <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={groupBy}
                        onChange={(_e, next) => next && setGroupBy(next)}
                        aria-label={intl.formatMessage({
                            id: "activity.group_by",
                            defaultMessage: "Group by",
                        })}
                        sx={segmentedSx}
                    >
                        <ToggleButton value={BY_IMPACT}>
                            <FormattedMessage id="activity.by_impact" defaultMessage="Impact" />
                        </ToggleButton>
                        <ToggleButton value={BY_SEGMENT}>
                            <FormattedMessage id="activity.by_segment" defaultMessage="Segment" />
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Stack>
            </Stack>

            {/* ── The grouped feed. Within every section, soonest first. ── */}
            {sections.length === 0 ? (
                <Paper
                    variant="outlined"
                    sx={{ p: 6, borderRadius: 2, textAlign: "center", color: "text.secondary" }}
                >
                    <Typography>
                        <FormattedMessage
                            id="activity.none_match"
                            defaultMessage="No activities match this filter."
                        />
                    </Typography>
                </Paper>
            ) : (
                <Stack spacing={2}>
                    {sections.map((section) => {
                        const empty = section.activities.length === 0;
                        // Only worth calling out under "All": an impact with no DONE
                        // activities is not an impact with nothing planned, and
                        // saying so while a filter is on would be a lie.
                        // A genuine gap: this impact has NO activities at all, whichever
                        // tab you are on. Judged on the UNFILTERED total, so "none Done
                        // yet" is never mistaken for it. It matters most under
                        // Outstanding, where an impact nobody has planned anything for is
                        // the thing you came to find.
                        const nothingPlanned =
                            groupBy === BY_IMPACT && section.totalAll === 0;
                        return (
                            <Paper
                                key={section.key}
                                variant="outlined"
                                sx={{
                                    p: 2,
                                    borderRadius: 2,
                                    ...(nothingPlanned
                                        ? { borderColor: OVERDUE.color, backgroundColor: OVERDUE.bg }
                                        : {}),
                                }}
                            >
                                <Stack
                                    direction="row"
                                    spacing={2}
                                    alignItems="center"
                                    justifyContent="space-between"
                                    flexWrap="wrap"
                                    useFlexGap
                                    sx={{ mb: empty ? 0 : 1.5 }}
                                >
                                    <Box
                                        sx={{
                                            minWidth: 0,
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 0.5,
                                        }}
                                    >
                                        {/* A control of its own rather than the whole header: the
                                            label is already a button when it links to an impact,
                                            and nesting buttons is invalid markup. */}
                                        {!empty && (
                                            <IconButton
                                                size="small"
                                                onClick={() => toggleSection(section.key)}
                                                aria-expanded={isSectionOpen(section.key)}
                                                aria-label={intl.formatMessage(
                                                    isSectionOpen(section.key)
                                                        ? {
                                                              id: "activity.collapse_section",
                                                              defaultMessage: "Collapse {section}",
                                                          }
                                                        : {
                                                              id: "activity.expand_section",
                                                              defaultMessage: "Expand {section}",
                                                          },
                                                    { section: section.label }
                                                )}
                                                sx={{ color: "text.secondary" }}
                                            >
                                                {isSectionOpen(section.key) ? (
                                                    <ExpandLessIcon fontSize="small" />
                                                ) : (
                                                    <ExpandMoreIcon fontSize="small" />
                                                )}
                                            </IconButton>
                                        )}
                                        {section.impactId ? (
                                            <MuiLink
                                                component="button"
                                                underline="hover"
                                                onClick={() => onOpenImpact(section.impactId)}
                                                sx={{ color: "#00438E", fontWeight: 700, textAlign: "left" }}
                                            >
                                                {section.label}
                                            </MuiLink>
                                        ) : (
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                {section.label}
                                            </Typography>
                                        )}
                                        {section.sublabel && (
                                            <Typography
                                                variant="caption"
                                                sx={{ color: "text.secondary", display: "block" }}
                                            >
                                                {section.sublabel}
                                            </Typography>
                                        )}
                                    </Box>

                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                        {nothingPlanned && (
                                            <>
                                                <Chip
                                                    size="small"
                                                    icon={<ErrorOutlineIcon />}
                                                    label={intl.formatMessage({
                                                        id: "activity.no_activities_yet",
                                                        defaultMessage: "No activities yet",
                                                    })}
                                                    sx={{
                                                        backgroundColor: "#fff",
                                                        color: OVERDUE.color,
                                                        fontWeight: 600,
                                                        "& .MuiChip-icon": { color: OVERDUE.color },
                                                    }}
                                                />
                                                {/* The note names the gap; Add closes it
                                                    without leaving the row. */}
                                                {canEdit && (
                                                <AddActivityButton
                                                    section={section}
                                                    onAdd={openAddFor}
                                                />
                                                )}
                                            </>
                                        )}
                                        {/* The same Add on an impact that ALREADY has
                                            activities. Only the zero-activity row
                                            used to offer one, which made "add an
                                            activity" look like something you could
                                            only do to an empty impact — the one case
                                            where you are least likely to want a
                                            second. Every impact section carries it
                                            now; `nothingPlanned` above renders its
                                            own alongside the gap chip. */}
                                        {canEdit && !nothingPlanned && section.impactId != null && (
                                            <AddActivityButton
                                                section={section}
                                                onAdd={openAddFor}
                                            />
                                        )}
                                        {canEdit && groupBy === BY_SEGMENT && section.impacts?.length > 0 && (
                                            <AddActivityButton section={section} onAdd={openAddFor} />
                                        )}
                                        {!empty && (
                                            <ProgressMeter
                                                done={section.counts.done}
                                                total={section.counts.total}
                                                width={140}
                                            />
                                        )}
                                    </Stack>
                                </Stack>

                                {/* Only reachable under "All", and only when the
                                    rollup claims a total the feed has no rows for.
                                    A status tab can no longer land here — it drops
                                    an impact nothing matched instead of heading a
                                    section over nothing. Kept for that one
                                    disagreement, which otherwise renders as a
                                    header with no rows and no chevron: exactly
                                    like a collapse that will not open. */}
                                {/* The inline form, in the section it was opened
                                    from and OUTSIDE the Collapse below — a
                                    collapsed section would otherwise swallow the
                                    form you just asked for, and coupling "open the
                                    form" to "expand the section" makes one click
                                    do two things.

                                    It names the impact when it does not ask for
                                    one; only a segment section holding several
                                    impacts passes `impacts`, which puts the
                                    picker back, limited to that section. */}
                                {addFor?.sectionKey === section.key && (
                                    <Paper
                                        variant="outlined"
                                        sx={{ p: 2, mt: 1.5, mb: 1, borderRadius: 2, backgroundColor: "#fff" }}
                                    >
                                        {/* NOT the uppercase eyebrow the other
                                            section labels use. That style is for
                                            one or two fixed words; run an impact
                                            name through it and you get a line of
                                            letterspaced capitals nobody can scan.
                                            Sentence case, with the impact in
                                            medium weight so the eye lands on the
                                            part that changes. */}
                                        <Typography
                                            variant="body2"
                                            sx={{ color: "text.secondary", mb: 2 }}
                                        >
                                            {addFor.impacts ? (
                                                <FormattedMessage
                                                    id="activity.new_in_segment"
                                                    defaultMessage="New activity in {segment}"
                                                    values={{
                                                        segment: (
                                                            <Box
                                                                component="span"
                                                                sx={{ fontWeight: 600, color: "text.primary" }}
                                                            >
                                                                {addFor.segmentLabel}
                                                            </Box>
                                                        ),
                                                    }}
                                                />
                                            ) : (
                                                <FormattedMessage
                                                    id="activity.new_for_impact"
                                                    defaultMessage="New activity for {impact}"
                                                    values={{
                                                        impact: (
                                                            <Box
                                                                component="span"
                                                                sx={{ fontWeight: 600, color: "text.primary" }}
                                                            >
                                                                {addFor.impactLabel}
                                                            </Box>
                                                        ),
                                                    }}
                                                />
                                            )}
                                        </Typography>
                                        <ActivityForm
                                            // Only a multi-impact segment section passes
                                            // these, which is what turns the picker on.
                                            impacts={addFor.impacts || undefined}
                                            saving={saving}
                                            onSave={handleAdd}
                                            onCancel={() => setAddFor(null)}
                                        />
                                    </Paper>
                                )}

                                {empty && !nothingPlanned && (
                                    <Typography
                                        variant="body2"
                                        sx={{ color: "text.disabled", fontStyle: "italic", mt: 1 }}
                                    >
                                        {/* A segment combination seeded on "All" is
                                            empty because nothing is planned in it,
                                            not because a filter hid it. */}
                                        {groupBy === BY_SEGMENT && status === ALL_STATUSES ? (
                                            <FormattedMessage
                                                id="activity.none_in_segment"
                                                defaultMessage="Nothing planned here yet."
                                            />
                                        ) : (
                                            <FormattedMessage
                                                id="activity.none_match"
                                                defaultMessage="No activities match this filter."
                                            />
                                        )}
                                    </Typography>
                                )}

                                {!empty && (
                                    <Collapse in={isSectionOpen(section.key)} unmountOnExit>
                                    <Stack spacing={1}>
                                        {section.activities.map((activity) => (
                                            <ActivityRow
                                                key={activity.id}
                                                activity={activity}
                                                showImpact={groupBy !== BY_IMPACT}
                                                busy={busyId === activity.id}
                                                onStatus={handleStatus}
                                                onAccept={handleAccept}
                                                onReject={handleReject}
                                                onOpen={onOpenImpact}
                                                onDelete={handleDelete}
                                                readOnly={!canEdit}
                                                // Grouped by impact the section
                                                // header already carries the Add;
                                                // a second one per row would be
                                                // the same button N times.
                                                onAddForImpact={
                                                    groupBy === BY_IMPACT
                                                        ? undefined
                                                        : (row) =>
                                                              openAddFor({
                                                                  impactId: row.impact_id,
                                                                  impactLabel: row.impact_name,
                                                                  key: section.key,
                                                              })
                                                }
                                            />
                                        ))}
                                    </Stack>
                                    </Collapse>
                                )}
                            </Paper>
                        );
                    })}
                </Stack>
            )}
        </Box>
    );
}
